import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { resolveImageUrl } from '../context/SettingsContext';

export const EXCLUSIVE_DEFAULT_IMAGE =
  'https://customer-assets-jt897jd0.emergentagent.net/job_34531605-61c4-451d-9ee7-b24843ceca0d/artifacts/7e3bd8d9d005ce06_ufo-tag-kl.jpg';

const DEFAULT_FEATURES = [
  { icon: 'Crown', title: 'Exklusive Modelle', text: 'Viele Designs nur bei uns erhältlich.', color: 'violet' },
  { icon: 'Heart', title: 'Mit Liebe zum Detail', text: 'Aufwendig gestaltet und hochwertig verarbeitet.', color: 'pink' },
  { icon: 'Layers', title: 'Über 50 Attraktionen', text: 'Große Auswahl für viele Anlässe.', color: 'sky' },
];

const DEFAULTS = {
  enabled: true,
  eyebrow: 'Exklusiv bei uns',
  title: 'Exklusive und einzigartige Hüpfburgen',
  title_highlight: 'nur bei uns!',
  text:
    'Entdecke besondere Hüpfburgen mit liebevollen Details, starken Themenwelten und echten Highlights, die du nicht überall findest. Perfekt für Geburtstage, Einschulungen und Events in Schwerin und Umgebung.',
  image: EXCLUSIVE_DEFAULT_IMAGE,
  badge_icon: 'Sparkles',
  badge_text: '',
  badge_subtext: '',
  primary_label: 'Jetzt entdecken',
  primary_link: '/huepfburgen',
  secondary_label: 'Mehr erfahren',
  secondary_link: '',
  features: DEFAULT_FEATURES,
  bg_color: '#f0f9ff',
  title_color: '#4c1d95',
  highlight_color: '#db2777',
  button_color: '#ec4899',
  title_size: 'md',
  text_size: 'md',
};

const SECTION_BG = '#f0f9ff';

const TITLE_SIZE = {
  sm: 'text-2xl sm:text-3xl',
  md: 'text-3xl sm:text-4xl',
  lg: 'text-4xl sm:text-5xl',
};
const TEXT_SIZE = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

const CHIP = {
  violet: { ring: 'ring-violet-100', icon: 'bg-violet-100 text-violet-500' },
  pink: { ring: 'ring-pink-100', icon: 'bg-pink-100 text-pink-500' },
  sky: { ring: 'ring-sky-100', icon: 'bg-sky-100 text-sky-500' },
  amber: { ring: 'ring-amber-100', icon: 'bg-amber-100 text-amber-500' },
};

const Icon = ({ name, fallback = 'Sparkles', className }) => {
  const C = Icons[name] || Icons[fallback] || Icons.Sparkles;
  return <C className={className} aria-hidden />;
};

// Same gentle 3-bump wave language as CloudDivider, but drawn as an overlay:
// the neighbour colour fills only the top strip (wavy BOTTOM edge) so the image
// below appears to start exactly at the wave.
const TopWave = ({ color }) => (
  <div className="absolute top-0 left-0 w-full z-10 pointer-events-none" style={{ lineHeight: 0 }} aria-hidden>
    <svg className="block w-full h-10 md:h-16" viewBox="0 0 1440 100" preserveAspectRatio="none">
      <path
        fill={color}
        d="M0,0 L1440,0 L1440,25 C1280,25 1200,50 960,50 C720,50 640,25 480,25 C320,25 240,50 0,50 Z"
      />
    </svg>
  </div>
);

// Neighbour colour fills only the bottom strip (wavy TOP edge) so the image ends
// exactly at the lower wave.
const BottomWave = ({ color }) => (
  <div className="absolute bottom-0 left-0 w-full z-10 pointer-events-none" style={{ lineHeight: 0 }} aria-hidden>
    <svg className="block w-full h-12 md:h-20" viewBox="0 0 1440 100" preserveAspectRatio="none">
      <path
        fill={color}
        d="M0,100 L1440,100 L1440,75 C1280,75 1200,50 960,50 C720,50 640,75 480,75 C320,75 240,50 0,50 Z"
      />
    </svg>
  </div>
);

const ExclusiveSection = ({ cfg, topBg = '#ffffff', bottomBg = '#ffffff' }) => {
  const navigate = useNavigate();
  const c = { ...DEFAULTS, ...(cfg || {}) };
  if (c.enabled === false) return null;
  const features = (Array.isArray(c.features) && c.features.length ? c.features : DEFAULT_FEATURES).slice(0, 3);
  const img = resolveImageUrl(c.image) || EXCLUSIVE_DEFAULT_IMAGE;
  const titleSizeCls = TITLE_SIZE[c.title_size] || TITLE_SIZE.md;
  const textSizeCls = TEXT_SIZE[c.text_size] || TEXT_SIZE.md;

  const go = (link) => {
    if (!link) return;
    if (/^https?:\/\//.test(link)) window.open(link, '_blank', 'noopener');
    else navigate(link);
  };

  const Badge = () => (
    (c.badge_text || c.badge_subtext) ? (
      <div className="absolute top-8 left-6 lg:left-28 flex items-center gap-2.5 rounded-2xl bg-white/95 backdrop-blur px-3.5 py-2.5 shadow-lg" data-testid="exclusive-badge">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
          <Icon name={c.badge_icon} className="w-5 h-5" />
        </span>
        <span className="leading-tight">
          {c.badge_text && <span className="block text-sm font-bold text-neutral-900">{c.badge_text}</span>}
          {c.badge_subtext && <span className="block text-xs text-neutral-500">{c.badge_subtext}</span>}
        </span>
      </div>
    ) : null
  );

  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: c.bg_color || SECTION_BG }} data-testid="exclusive-section">
      {/* Left organic curve for the desktop image. Top & bottom stay FLAT so the
          wave overlays (below) define exactly where the image starts and ends. */}
      <svg width="0" height="0" className="absolute" aria-hidden>
        <defs>
          <clipPath id="exclCurve" clipPathUnits="objectBoundingBox">
            <path d="M0.14,0 L0.88,0 Q1,0 1,0.17 L1,0.83 Q1,1 0.88,1 L0.1,1 C-0.02,0.63 0.30,0.33 0.14,0 Z" />
          </clipPath>
        </defs>
      </svg>

      {/* Desktop image – aligned to the content container's right edge (ends
          where the boxes/text end, NOT at the viewport edge) with an organic
          left transition. */}
      <div className="hidden lg:block absolute inset-0 z-0 pointer-events-none">
        <div className="relative mx-auto h-full max-w-7xl">
          <div className="absolute inset-y-0 right-4 sm:right-6 lg:right-8 w-[48%]" style={{ clipPath: 'url(#exclCurve)' }}>
            <img src={img} alt={c.title || 'Exklusive Hüpfburg'} className="h-full w-full object-cover" data-testid="exclusive-image" />
            <Badge />
          </div>
        </div>
      </div>

      {/* Matching site waves – image starts at the top wave, ends at the bottom wave */}
      <TopWave color={topBg} />
      <BottomWave color={bottomBg} />

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-16 pb-8 lg:pb-10">
        <div className="lg:w-[50%]">
          {/* eyebrow + playful doodles */}
          <div className="relative inline-block mb-4">
            {c.eyebrow && (
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 shadow-md shadow-violet-900/5 ring-1 ring-violet-100" data-testid="exclusive-eyebrow">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-100">
                  <Icon name="Star" className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                </span>
                <span className="text-xs font-extrabold tracking-wide text-violet-900">{c.eyebrow}</span>
              </div>
            )}
            <Icons.Rocket className="hidden sm:block absolute -right-14 -top-2 w-6 h-6 text-violet-300 rotate-[18deg]" aria-hidden />
            <svg className="hidden sm:block absolute -right-6 -top-4 w-4 h-4 text-yellow-400" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M12 3v4M12 17v4M3 12h4M17 12h4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>

          <h2 className={`${titleSizeCls} font-bold tracking-tight leading-[1.12]`} style={{ color: c.title_color || '#4c1d95' }} data-testid="exclusive-title">
            {c.title}{c.title_highlight ? ' ' : ''}
            {c.title_highlight && (
              <span className="relative inline-block" style={{ color: c.highlight_color || '#db2777' }}>
                {c.title_highlight}
                <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 220 12" fill="none" preserveAspectRatio="none" aria-hidden>
                  <path d="M3 8C40 3 120 2 217 6" stroke="#facc15" strokeWidth="4" strokeLinecap="round" />
                </svg>
              </span>
            )}
          </h2>

          {c.text && (
            <p className={`mt-5 ${textSizeCls} text-neutral-600 leading-relaxed max-w-lg`} data-testid="exclusive-text">
              {c.text}
            </p>
          )}

          {/* Mobile / tablet image – less landscape, more balanced crop */}
          <div className="lg:hidden relative mt-6 rounded-[1.5rem] overflow-hidden shadow-lg">
            <img src={img} alt={c.title || 'Exklusive Hüpfburg'} className="w-full aspect-[4/3] object-cover" />
            <Badge />
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
            {c.primary_label && (
              <button type="button" onClick={() => go(c.primary_link)}
                className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-bold text-white shadow-lg shadow-black/10 transition-transform hover:scale-[1.03] active:scale-95"
                style={{ backgroundColor: c.button_color || '#ec4899' }}
                data-testid="exclusive-primary-btn">
                {c.primary_label}
                <Icon name="ArrowRight" className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
            )}
            {c.secondary_label && (
              <button type="button" onClick={() => go(c.secondary_link || c.primary_link)}
                className="text-base font-semibold text-violet-900/80 underline underline-offset-4 decoration-2 decoration-violet-300 hover:decoration-violet-600"
                data-testid="exclusive-secondary-btn">
                {c.secondary_label}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Feature chips – compact 3-up: vertical on mobile, horizontal on desktop */}
      <div className="relative z-30 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-3 lg:pb-3">
        <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:gap-5">
          {features.map((f, i) => {
            const s = CHIP[f.color] || CHIP.violet;
            return (
              <div key={i} className={`flex flex-col sm:flex-row items-center text-center sm:text-left gap-1.5 sm:gap-2.5 rounded-xl sm:rounded-2xl bg-white ring-1 ${s.ring} shadow-[0_6px_16px_-8px_rgba(16,24,40,0.16)] px-2 py-2.5 sm:px-3.5 sm:py-3`} data-testid={`exclusive-feature-${i}`}>
                <span className={`flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-lg sm:rounded-xl ${s.icon}`}>
                  <Icon name={f.icon} className="w-4 h-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] sm:text-sm font-bold text-neutral-900 leading-tight">{f.title}</span>
                  {f.text && <span className="hidden sm:block text-xs text-neutral-500 leading-snug mt-0.5">{f.text}</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ExclusiveSection;
